import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../../assets/images/cup-artie.png';
import { Card } from "@/components/ui/card";
import { Loader2, Check } from "lucide-react";
import { useOnboarding } from "@/context/OnboardingContext";
import { useArtieScore } from "@/hooks/use-artie-score";
import { useInventoryMode } from "@/hooks/useInventoryMode";
import { toast } from "sonner";
import { useAuthStore } from "@/stores/authStore";
import { apiClient } from "@/lib/api";

export default function LoadingV2() {
    const navigate = useNavigate();
    const { data } = useOnboarding();
    const { checkAuth } = useAuthStore();

    // The signup wizard's waiting screen. Its step timer deliberately STOPS at the last step and
    // holds there until scoreData arrives (see the interval below), so with no score being computed
    // a brand-new creator would sit on it indefinitely — the worst place in the product to hang,
    // because it is the first thing they ever see.
    //
    // This screen was missed on the first pass at the thank-you redirect: the two waiting screens I
    // found were the new-project wizard's loader and the pending page, and the pending page turns out
    // to be on a route nothing links to. Signup goes through here instead.
    const inventoryMode = useInventoryMode();

    const { scoreData, error, refetch } = useArtieScore(data.projectId);
    const [currentStep, setCurrentStep] = useState(1);
    const [progressPercent, setProgressPercent] = useState(0);

    // Total steps
    const TOTAL_STEPS = 4;
    // Time per step in ms (increased duration)
    const STEP_DURATION = 15000;

    useEffect(() => {
        if (!inventoryMode) return;
        let alive = true;

        // MARK ONBOARDING COMPLETE FIRST. This is the only place signup can do it, and skipping it
        // does not merely lose a flag — it locks the creator out of the product. ProtectedRoute:168
        // sends any user without onboarding_completed back to the wizard from every route that is
        // not itself an onboarding page, so a creator who finished signup in inventory mode could
        // not reach the thank-you page OR the dashboard. They were returned to "Hi, I'm Artie."
        // forever, with a finished project and a complete placement run sitting in the database.
        //
        // It used to happen in the completion effect below, which waits for scoreData — and in
        // inventory mode no score is ever computed, so that effect never runs.
        (async () => {
            try {
                await apiClient.updateCurrentUser({ onboarding_completed: true });
                await checkAuth();
            } catch (error) {
                // Non-fatal: /upload-thanks is exempt in ProtectedRoute precisely so this failure
                // strands nobody. They land correctly; the flag is retried next sign-in.
                console.error("Could not mark onboarding complete:", error);
            }
            if (!alive) return;
            navigate(
                data.projectId ? `/upload-thanks?projectId=${data.projectId}` : '/upload-thanks',
                { replace: true },
            );
        })();

        return () => { alive = false; };
    }, [inventoryMode, data.projectId, navigate, checkAuth]);

    useEffect(() => {
        // Trigger calculation on mount
        // refetch();
    }, []);

    useEffect(() => {
        let stepInterval: NodeJS.Timeout;
        let progressInterval: NodeJS.Timeout;

        // Function to handle normal progression
        const runProgress = () => {
            // Update steps according to duration
            stepInterval = setInterval(() => {
                setCurrentStep((prev) => {
                    // If we have score data, we can proceed to completion
                    if (scoreData) {
                        return Math.min(prev + 1, TOTAL_STEPS + 1); // +1 to trigger completion
                    }
                    // Otherwise, stop at the last step (wait for scoreData)
                    return Math.min(prev + 1, TOTAL_STEPS);
                });
            }, STEP_DURATION);

            // Smooth progress bar animation
            // We want the progress bar to fill up roughly in sync with the steps
            // Each step is 25% (100/4)
            // Calculate increment based on duration: 25% / (STEP_DURATION / 50ms)
            const increment = 25 / (STEP_DURATION / 50);

            progressInterval = setInterval(() => {
                setProgressPercent((prev) => {
                    const targetPercent = Math.min(currentStep * 25, 100);
                    
                    // If calculation is done, we accelerate to 100%
                    if (scoreData) {
                        const remaining = 100 - prev;
                        // Accelerate: move 10% of remaining distance or at least 5% per tick
                        return Math.min(prev + Math.max(remaining * 0.2, 5), 100);
                    }

                    // If we are at the last step and waiting, stall at 90-95%
                    if (currentStep === TOTAL_STEPS && !scoreData) {
                        return Math.min(prev + 0.1, 95);
                    }

                    // Normal progression
                    if (prev < targetPercent) {
                        return prev + increment;
                    }
                    return prev;
                });
            }, 50); // Update every 50ms for smooth animation
        };

        runProgress();

        return () => {
            clearInterval(stepInterval);
            clearInterval(progressInterval);
        };
    }, [currentStep, scoreData]);

    // Handle completion navigation
    useEffect(() => {
        // If we have score data AND visual progress is complete (reached end of all steps)
        if (scoreData && progressPercent >= 100) {
            // Small delay to register the "complete" visual state
            const timer = setTimeout(async () => {
                // Mark onboarding as completed now that processing is done
                try {
                    await apiClient.updateCurrentUser({ onboarding_completed: true });
                    await checkAuth(); // Refresh user state
                } catch (error) {
                    console.error("Failed to mark onboarding as complete:", error);
                    // Continue anyway - don't block navigation
                }
                
                // Use replace: true to prevent this loading page from appearing in browser history
                // This prevents the brief redirect when user clicks back button
                navigate(`/initial-assessment?projectId=${data.projectId}`, { replace: true });
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [scoreData, progressPercent, navigate, data.projectId, checkAuth]);

    // Handle error state
    useEffect(() => {
        // INVENTORY MODE IS NOT AN ERROR. use-artie-score reports "scoring is turned off" through the
        // same `error` field it uses for real failures, because that is what makes the twelve screens
        // reading this hook stop waiting for a score that is never coming. This screen then treated it
        // as a failed analysis: it showed the creator a red "Something went wrong" toast and redirected
        // to /onboarding-v2/page6 — a route that does not exist — so a SUCCESSFUL upload ended on a 404
        // or bounced back to the first page of the wizard they had just finished.
        //
        // The effect above already owns the destination in this mode. Returning here leaves it to it.
        if (inventoryMode) return;

        if (error) {
            console.error("Error in loading:", error);
            toast.error("Something went wrong with the analysis. Please try again.", {
                style: {
                    background: '#ef4444',
                    color: 'white',
                    border: 'none'
                }
            });
            // Go back to previous page on error so they can retry
            navigate('/onboarding-v2/page6');
        }
    }, [error, inventoryMode, navigate]);

    return (
        <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center font-sans">

            <main className="relative z-10 w-full flex flex-col items-center justify-center min-h-screen py-12">

                {/* Logo */}
                <div className="mb-8 relative">
                    <div className="absolute inset-0 bg-[oklch(0.97_0.01_60)] blur-xl opacity-50"></div>
                    <img
                        src={logo}
                        alt="Artie"
                        className="w-48 h-48 object-contain relative z-10"
                    />
                </div>

                <div className="flex flex-col items-center max-w-2xl w-full px-4 animate-in slide-in-from-bottom-4 duration-700">
                    <h1 className="text-4xl md:text-5xl font-bold mb-6 text-center text-[oklch(0.2686_0_0)] font-cabinet tracking-tight leading-tight">
                        Giving your script the <br /> sniff test
                    </h1>
                    <p className="text-lg text-[oklch(0.55_0_0)] text-center mb-10 max-w-xl font-ibm leading-relaxed">
                        Hang tight. It’ll be a few minutes. I’m giving your script an early read to spot brand-friendly moments, audience signals, and overall potential. Promise I won’t eat it.
                    </p>

                    <Card className="w-full bg-white shadow-xl border border-[oklch(0.92_0_0)] overflow-hidden p-8 rounded-[12px] relative">
                        {/* Progress Bar Top */}
                        <div
                            className="absolute top-0 left-0 h-1 bg-black transition-all duration-500 ease-in-out"
                            style={{ width: `${progressPercent}%` }}
                        ></div>

                        <div className="space-y-8">
                            <StepItem
                                stepNumber={1}
                                currentStep={currentStep}
                                title="Warming up"
                                description="Getting everything ready"
                            />
                            <StepItem
                                stepNumber={2}
                                currentStep={currentStep}
                                title="Reading your script"
                                description="Looking for moments that feel natural and story-true"
                            />
                            <StepItem
                                stepNumber={3}
                                currentStep={currentStep}
                                title="Exploring brand fit"
                                description="Noticing where brands could belong without breaking the story"
                            />
                            <StepItem
                                stepNumber={4}
                                currentStep={currentStep}
                                title="Pulling together early insights"
                                description="Highlights you’ll see right away"
                            />
                        </div>
                    </Card>
                </div>
            </main>
        </div>
    );
}

function StepItem({ stepNumber, currentStep, title, description }: { stepNumber: number, currentStep: number, title: string, description: string }) {
    const isCompleted = currentStep > stepNumber;
    const isActive = currentStep === stepNumber;
    const isPending = currentStep < stepNumber;

    return (
        <div className={`flex gap-4 ${isPending ? 'opacity-40' : 'opacity-100'} transition-opacity duration-300`}>
            <div className="shrink-0">
                {isActive ? (
                    <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center text-white">
                        <Loader2 className="w-5 h-5 animate-spin" />
                    </div>
                ) : isCompleted ? (
                    <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center text-white">
                        <Check className="w-5 h-5" />
                    </div>
                ) : (
                    <div className="w-8 h-8 rounded-full border border-[oklch(0.85_0_0)] flex items-center justify-center text-[oklch(0.55_0_0)] font-medium text-sm">
                        {stepNumber}
                    </div>
                )}
            </div>
            <div>
                <h3 className={`text-base ${isActive || isCompleted ? 'font-bold text-[oklch(0.2686_0_0)]' : 'font-medium text-[oklch(0.2686_0_0)]'}`}>
                    {title}
                </h3>
                <p className="text-[oklch(0.55_0_0)] text-sm mt-0.5">{description}</p>
            </div>
        </div>
    );
}